import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import { Carousel, CarouselDotButtons, useCarousel } from '@/components/carousel';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { GridSection } from '@/types/sections/GridSection';
import { Button, Grid, Paper, useTheme } from '@mui/material';
import { CardPropertiesItem } from '@/types/CardPropertiesItem';
import React from 'react';
import { getCtaUrl } from '@/types/Cta';
import Icon, { getIconName } from '@/components/Icon/Icon';

type GridProps = BoxProps & {
  section: GridSection;
};

export default function GridComponent({ sx, section, ...other }: GridProps) {
  const theme = useTheme();
  const layout = (section.layout ?? 'card') as 'card' | 'simple' | 'carousel';

  const carousel = useCarousel({
    align: 'start',
    containScroll: false,
    slidesToShow: { xs: 1, sm: 2.5 },
    slideSpacing: '50px',
  });

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography variant="h3" sx={{ my: 3, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div variants={varFade('inUp')}>
            <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <m.div variants={varFade('inUp')}>
          {layout === 'carousel' ? (
            <Box sx={{ position: 'relative', alignItems: 'center', pt: 4 }}>
              <Carousel carousel={carousel} slotProps={{ slide: { display: 'flex' } }}>
                {section?.cards?.map((item, index) => (
                  <Paper
                    key={`carousel-item-${index}`}
                    sx={{
                      p: 2,
                      pt: 3,
                      pb: 3,
                      height: '100%',
                      textAlign: 'start',
                      borderRadius: 0,
                      boxShadow: theme.vars.customShadows.card,
                      display: 'flex',
                      flexDirection: 'column',
                      width: 1,
                      flex: 1,
                    }}>
                    <GridItemCard
                      index={index}
                      item={{
                        id: `carousel-item-${index}`,
                        ...item,
                      }}
                    />
                  </Paper>
                ))}
              </Carousel>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'end',
                  flexDirection: 'column',
                  bgcolor: 'background.neutral',
                }}>
                <CarouselDotButtons
                  variant="rounded"
                  scrollSnaps={carousel.dots.scrollSnaps}
                  selectedIndex={carousel.dots.selectedIndex}
                  onClickDot={carousel.dots.onClickDot}
                  sx={{ color: 'primary.main' }}
                />
              </Box>
            </Box>
          ) : (
            <Grid container spacing={6} sx={{ width: 1, pt: 4 }}>
              {section?.cards?.map((item, index) => (
                <Grid key={`grid-item-${index}`} size={{ xs: 12, sm: 12, md: 6 }}>
                  <Paper
                    sx={{
                      p: 2,
                      pt: 3,
                      pb: 3,
                      height: '100%',
                      textAlign: 'start',
                      borderRadius: 0,
                      boxShadow: theme.vars.customShadows.card,
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {layout === 'card' ? (
                      <GridItemCard
                        index={index}
                        item={{
                          id: `carousel-item-${index}`,
                          ...item,
                        }}
                      />
                    ) : (
                      <GridItemSimpleCard
                        index={index}
                        item={{
                          id: `carousel-item-${index}`,
                          ...item,
                        }}
                      />
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </m.div>

        {section?.cta1 && (section?.cta1.title || section.cta1.icon) && (
          <m.div variants={varFade('inUp')}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              href={getCtaUrl(section.cta1)}
              sx={{
                mt: 4,
                borderRadius: 0,
              }}
              startIcon={
                section.cta1.icon && (
                  <Icon
                    name={getIconName(section.cta1.icon, 'unknown')}
                    color={theme.palette.common.white}
                  />
                )
              }>
              {section?.cta1.title}
            </Button>
          </m.div>
        )}
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type GridItemProps = {
  index: number;
  item: { id: string } & CardPropertiesItem;
};

function GridItemSimpleCard({ item, index }: GridItemProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
        alignItems: 'flex-start',
        borderRadius: 0,
        overflow: 'hidden',
        textAlign: 'start',
        height: '100%',
      }}>
      {/* Text Content */}
      <Box
        sx={{
          flex: 1,
          paddingLeft: index % 2 === 0 ? 0 : 2,
          paddingRight: index % 2 === 0 ? 2 : 0,
        }}>
        <Typography variant="h4" sx={{ color: 'primary.main' }}>
          {item.title}
        </Typography>

        {item.properties &&
          item.properties.map((property, propIndex) => (
            <React.Fragment key={propIndex}>
              <Typography sx={{ maxWidth: 640, pt: 1, color: 'primary.main' }}>
                {property.title}
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 640, color: 'text.secondary' }}>
                {property.subtitle}
              </Typography>
            </React.Fragment>
          ))}
      </Box>

      {/* Image */}
      <Box
        component="img"
        alt={item.title}
        src={item.image?.url || ''}
        sx={{
          flexShrink: 0,
          width: '50%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );
}

function GridItemCard({ item, index }: GridItemProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column', // Stack image on top and text below
        alignItems: 'flex-start',
        borderRadius: 0,
        overflow: 'hidden',
        textAlign: 'start',
        height: '100%',
      }}>
      {/* Image */}
      <Box
        component="img"
        alt={item.title}
        src={item.image?.url || ''}
        sx={{
          width: '100%', // Full width for the image
          position: 'relative',
          aspectRatio: '4/3',
          objectFit: 'cover', // Ensures the image fills the square without distortion
          top: 0,
          left: 0,
          marginBottom: 2,
        }}
      />

      {/* Text Content */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main' }}>
            {item.title}
          </Typography>

          {item.properties &&
            item.properties.map((property, propIndex) => (
              <React.Fragment key={propIndex}>
                <Typography
                  variant="body2"
                  sx={{ pt: 2, fontWeight: 'fontWeightSemiBold', color: 'primary.main' }}>
                  {property.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {property.subtitle}
                </Typography>
              </React.Fragment>
            ))}
        </Box>

        {item?.cta1 && (item?.cta1.title || item.cta1.icon) && (
          <m.div variants={varFade('inUp')} style={{ marginTop: 'auto', paddingTop: 16 }}>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              href={getCtaUrl(item.cta1)}
              sx={{
                borderRadius: 0,
              }}
              startIcon={
                item.cta1.icon && (
                  <Icon name={getIconName(item.cta1.icon, 'unknown')} color={'common.white'} />
                )
              }>
              {item?.cta1.title}
            </Button>
          </m.div>
        )}
      </Box>
    </Box>
  );
}
